import { agentCount, home3AgentName, type Home3Destination, type Home3Event } from './home3-feed';
import { outreachCardSummaryLine } from './outreach-card';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * What a canvas activity-panel row types in (`Home3ActivityPanel`): its title, or the key standing in for one — a
 * file's after "File created:". Kept out of the component so a unit test can reach it without the app's graph.
 */
export function panelRowText(event: Home3Event, t: (key: string, params?: Record<string, string>) => string): string {
  // A sent thread is titled by the PERSON (the board's row); here the person is the name beside the time, so the
  // row says what the board's card line says: their latest words, else ours as "You: …".
  const sub = event.subtitle;
  if (sub.kind === 'contact' && sub.summary && sub.summary.kind !== 'none' && sub.summary.text) {
    const line = outreachCardSummaryLine(sub.summary);
    return 'text' in line ? line.text : t(line.key, line.params);
  }
  const title = event.titleKey ? t(event.titleKey) : event.title;
  return event.item?.kind === 'file' ? t('home.overview.panel.fileCreated', { name: title }) : title;
}

/** What a row's link reads, by its category — for a row that is not a task (a task row reads "Open task",
 *  whatever category it is filed under: a routine's run opens the task). */
const LINK_KEY: Record<Home3Destination, string> = {
  tasks: 'home.map.openTask',
  goals: 'home.map.openGoal',
  files: 'home.map.openFile',
  outreach: 'home.map.viewMessage',
  routines: 'home.map.openRoutine',
  apps: 'home.map.openApp',
};

/** The key of a panel row's link — the desktop panel's and the phones' Recent activity sheet's alike. */
export function panelRowLinkKey(event: Home3Event): string {
  return event.taskId ? 'home.map.openTask' : LINK_KEY[event.destination];
}

/** Who a panel row is from: the agent; the Maestro for its own rows and a task nobody has been put on yet (it
 *  wears the Maestro's mark too); the person for a message row — a sent thread carries them as its title. */
export function panelRowWho(event: Home3Event, agentNames: Record<string, string>, maestroName: string, t: Translate): string {
  const sub = event.subtitle;
  if (sub.kind === 'agent') return home3AgentName(event, agentNames);
  if (sub.kind === 'maestro' || sub.kind === 'none') return maestroName;
  if (sub.kind === 'contact') {
    if (sub.name || sub.nameKey) return sub.name || t(sub.nameKey as string);
    return sub.summary ? (event.titleKey ? t(event.titleKey) : event.title) : '';
  }
  return '';
}

/**
 * The Recent activity sheet's subtitle (the phones' and mobile web's Live Updates chip opens it): how many distinct
 * agents are behind its rows, "since your last visit" — or, with no agent among them, only the "since". The same
 * rule on iOS and Android (`ActivityMapLiveSheet`).
 */
export function liveSheetSubtitle(rows: Home3Event[], t: Translate): string {
  const n = agentCount(rows);
  if (n === 0) return t('home.overview.live.since');
  const agents = t(n === 1 ? 'home.overview.live.agentsOne' : 'home.overview.live.agentsOther', { count: n });
  return t('home.overview.live.subtitle', { agents });
}

/** The Live Updates chip shows with the headline, and only while there is something to list (the user's call). */
export function liveChipShown(updates: number | null): boolean {
  return updates !== null && updates > 0;
}

/**
 * The "{updates} since your last visit" headline — the desktop canvas's activity panel's and the phone map's — as the
 * runs it is set in: ONE sentence for the translator, with the counted phrase ("12 new updates") spliced in where the
 * language puts it, so it can take its own weight. Null until the count is settled (no headline at all).
 *
 * At ZERO it is its own sentence, `home.overview.panel.headlineNone` ("No new updates since your last visit"), with
 * no number and no counted run (user decision 2026-11-03, with iOS/Android): "0 new updates since your last visit"
 * read as a count the reader was meant to act on. Only for a TRULY empty feed, though: `total` is how many there
 * are in all (the phone map's headline counts them all, so it defaults to `updates`). The desktop panel counts the
 * replay's position, so at the start of a replay of N > 0 it is 0 of N — the counted "0 new updates", with N about
 * to play, never "No new updates".
 */
export type PanelHeadline = { kind: 'none'; text: string } | { kind: 'count'; before: string; count: string; after: string };

export function panelHeadline(updates: number | null, t: Translate, total: number | null = updates): PanelHeadline | null {
  if (updates === null) return null;
  if (updates === 0 && !total) return { kind: 'none', text: t('home.overview.panel.headlineNone') };
  const count = t(updates === 1 ? 'home.overview.panel.updatesOne' : 'home.overview.panel.updatesOther', { count: updates });
  const mark = '\u0000';
  const [before = '', after = ''] = t('home.overview.panel.headline', { updates: mark }).split(mark);
  return { kind: 'count', before, count, after };
}
